import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import { Modal } from '../ui';
import menuSchedulingService from '../../services/menuSchedulingService';
import { createOccasionFromSchedule } from '../../services/unifiedPlanningService';
import SignedImage from '../SignedImage';
import { api } from '../../services/api';

/**
 * RecipeSearchModal Component
 * Opens when a day is clicked, allows searching and adding multiple recipes to that day
 */
const RecipeSearchModal = ({ 
    isOpen, 
    onClose, 
    selectedDate,
    onRecipeAdded,
    backgroundOption = 'tomato',
    existingMeals = []
}) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [searchType, setSearchType] = useState('name');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVibe, setSelectedVibe] = useState('');
    const [availableVibes, setAvailableVibes] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [selectedRecipes, setSelectedRecipes] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // AI Suggestions state
    const [suggestionVibe, setSuggestionVibe] = useState(''); // 'breakfast', 'lunch', 'dinner'
    const [aiSuggestions, setAiSuggestions] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [showAddRecipe, setShowAddRecipe] = useState(false); // Toggle between view and add modes

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedRecipes(new Set());
            setRecipes([]);
            setSearchQuery('');
            setSuggestionVibe('');
            setAiSuggestions(null);
            // If there are existing meals, show them first; otherwise show add recipe form
            setShowAddRecipe(existingMeals.length === 0);
        }
    }, [isOpen, existingMeals.length]);

    // Load available vibe tags when modal opens
    useEffect(() => {
        if (isOpen && searchType === 'vibe') {
            loadVibes();
        }
    }, [isOpen, searchType]);

    // Search when query changes
    useEffect(() => {
        if (isOpen && searchQuery && searchType !== 'vibe') {
            const timeoutId = setTimeout(() => {
                performSearch();
            }, 300);
            return () => clearTimeout(timeoutId);
        } else if (isOpen && searchType === 'vibe' && selectedVibe) {
            performSearch();
        }
    }, [searchQuery, searchType, selectedVibe, isOpen]);

    const loadVibes = async () => {
        if (!user) return;
        try {
            const token = await user.getIdToken();
            const vibes = await menuSchedulingService.getVibeTags(token);
            setAvailableVibes(vibes);
        } catch (err) {
            console.error('Error loading vibes:', err);
        }
    };

    const performSearch = async () => {
        if (!user || !selectedDate) return;

        setLoading(true);
        setError(null);

        try {
            const token = await user.getIdToken();
            const results = await menuSchedulingService.searchRecipes(
                searchQuery,
                searchType,
                selectedVibe,
                token
            );
            setRecipes(results);
        } catch (err) {
            console.error('Error searching recipes:', err);
            setError('Failed to search recipes');
        } finally {
            setLoading(false);
        }
    };

    const fetchAISuggestions = async (vibe) => {
        if (!user || !selectedDate) return;

        setLoadingSuggestions(true);
        setError(null);

        try {
            const token = await user.getIdToken();
            const response = await api.post('/menu-scheduling/ai-suggestions', {
                vibe: vibe,
                scheduled_date: selectedDate.toISOString().split('T')[0]
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAiSuggestions(response.data);
        } catch (err) {
            console.error('Error fetching AI suggestions:', err);
            setError('Failed to load AI suggestions');
        } finally {
            setLoadingSuggestions(false);
        }
    };

    const handleVibeSelect = (vibe) => {
        setSuggestionVibe(vibe);
        fetchAISuggestions(vibe);
    };

    const toggleRecipeSelection = (recipeId) => {
        const newSelected = new Set(selectedRecipes);
        if (newSelected.has(recipeId)) {
            newSelected.delete(recipeId);
        } else {
            newSelected.add(recipeId);
        }
        setSelectedRecipes(newSelected);
    };

    const handleConfirmAdd = async () => {
        if (!user || !selectedDate || selectedRecipes.size === 0) return;

        try {
            const token = await user.getIdToken();
            
            // Filter out AI suggestions (they have string IDs starting with "suggestion-")
            // Only schedule actual recipes (numeric IDs or numeric strings)
            const actualRecipeIds = Array.from(selectedRecipes).filter(id => {
                if (typeof id === 'number') return true;
                if (typeof id === 'string') {
                    // Check if it's a suggestion ID or a numeric string
                    if (id.startsWith('suggestion-')) return false;
                    // Check if it's a valid number
                    return !isNaN(parseInt(id, 10));
                }
                return false;
            });
            
            if (actualRecipeIds.length === 0) {
                setError('Please select actual recipes to schedule. AI suggestions need to be created as recipes first.');
                return;
            }
            
            const promises = actualRecipeIds.map(recipeId =>
                menuSchedulingService.scheduleMeal(
                    recipeId,
                    selectedDate,
                    null,
                    null,
                    null,
                    token
                )
            );

            await Promise.all(promises);

            // After all meals are scheduled, ensure an occasion exists for this date
            // This prevents race conditions when multiple meals are scheduled in parallel
            try {
                const dateKey = selectedDate instanceof Date 
                    ? selectedDate.toISOString().split('T')[0]
                    : selectedDate;
                
                // Format date name
                const dateForName = selectedDate instanceof Date ? selectedDate : new Date(selectedDate + 'T12:00:00');
                const occasionName = dateForName.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                });

                // This will check for existing occasion and only create if needed
                // Uses advisory lock in backend to prevent duplicates
                await createOccasionFromSchedule(dateKey, dateKey, occasionName, token);
            } catch (err) {
                // Log but don't fail - occasion creation is a nice-to-have
                console.error('Failed to ensure occasion exists:', err);
            }

            if (onRecipeAdded) {
                onRecipeAdded();
            }
            
            onClose();
        } catch (err) {
            console.error('Error adding recipes to schedule:', err);
            setError('Failed to add recipes to schedule');
        }
    };

    const handleCreateRecipe = () => {
        navigate('/create-recipe', {
            state: {
                scheduledDate: selectedDate,
                returnTo: '/menu-scheduling'
            }
        });
        onClose();
    };

    const formatDateUppercase = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).toUpperCase();
    };

    if (!isOpen || !selectedDate) return null;

    const getBackgroundStyle = () => {
        const backgroundImages = {
            tomato: '/images/menuScheduling/tomato_bg.png',
            lemon: '/images/menuScheduling/lemon_bg.png',
            spinach: '/images/menuScheduling/spinach_bg.png'
        };

        return {
            backgroundImage: `url(${backgroundImages[backgroundOption]})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        };
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            maxWidth="max-w-5xl"
            backdropOpacity={1}
            backdropStyle={getBackgroundStyle()}
            className="p-0"
        >
            <div className="p-8 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="mb-6">
                    <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-40 mb-2">
                        {formatDateUppercase(selectedDate)}
                    </p>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <img
                                src={`/images/menuScheduling/${backgroundOption}_icon.png`}
                                alt=""
                                className="w-8 h-8"
                                style={{ 
                                    imageRendering: 'pixelated',
                                    imageRendering: '-moz-crisp-edges',
                                    imageRendering: 'crisp-edges'
                                }}
                            />
                            <h2 className="font-sans text-3xl font-medium text-[#1b263b]">
                                {showAddRecipe ? 'Add Recipe' : 'Scheduled Meals'}
                            </h2>
                        </div>
                        {!showAddRecipe && (
                            <button
                                onClick={() => setShowAddRecipe(true)}
                                className="w-8 h-8 flex items-center justify-center bg-[#1b263b] text-white hairline hover:opacity-60 transition-opacity font-mono text-lg"
                                aria-label="Add recipe"
                            >
                                +
                            </button>
                        )}
                    </div>
                    {showAddRecipe && (
                        <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                            Search for recipes or get AI suggestions
                        </p>
                    )}
                </div>

                {/* Existing Scheduled Meals */}
                {!showAddRecipe && existingMeals.length > 0 && (
                    <div className="mb-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {existingMeals.map((meal) => (
                                <div
                                    key={meal.id}
                                    className="bg-neutral-0 hairline p-3 relative group"
                                >
                                    {/* Delete Button */}
                                    <button
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Remove "${meal.recipe_name}" from this day?`)) {
                                                try {
                                                    const token = await user.getIdToken();
                                                    await menuSchedulingService.removeScheduledMeal(meal.id, token);
                                                    // Refresh meals by calling onRecipeAdded (which reloads scheduled meals)
                                                    if (onRecipeAdded) {
                                                        onRecipeAdded();
                                                    }
                                                } catch (err) {
                                                    console.error('Error deleting scheduled meal:', err);
                                                    setError('Failed to delete scheduled meal');
                                                }
                                            }
                                        }}
                                        className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 text-white hover:bg-red-600 transition-colors font-mono text-sm border-none opacity-0 group-hover:opacity-100"
                                        aria-label="Delete scheduled meal"
                                        title="Delete"
                                    >
                                        ×
                                    </button>
                                    <div className="aspect-square w-full mb-2 bg-neutral-50 hairline overflow-hidden">
                                        <SignedImage
                                            src={meal.recipe_images && meal.recipe_images.length > 0 ? meal.recipe_images[0] : null}
                                            alt={meal.recipe_name}
                                            className="w-full h-full object-cover"
                                            category={meal.recipe_category}
                                        />
                                    </div>
                                    <h3 className="font-sans text-sm font-medium text-neutral-900 mb-1 line-clamp-2">
                                        {meal.recipe_name}
                                    </h3>
                                    {meal.meal_type && (
                                        <p className="font-mono text-xs text-neutral-900 opacity-60 uppercase">
                                            {meal.meal_type}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Recipe Form - Only show when showAddRecipe is true */}
                {showAddRecipe && (
                    <>

                {/* AI Suggestions Section */}
                <div className="mb-6 bg-neutral-50 hairline p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-sans text-xl font-medium text-[#1b263b]">
                            AI Suggestions
                        </h3>
                        {!suggestionVibe && (
                            <button
                                onClick={handleCreateRecipe}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all font-mono text-sm uppercase tracking-wider flex items-center gap-2"
                            >
                                <span className="text-lg">+</span>
                                <span>Create New Recipe</span>
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2 mb-4">
                        {['breakfast', 'lunch', 'dinner'].map((vibe) => (
                            <button
                                key={vibe}
                                onClick={() => handleVibeSelect(vibe)}
                                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                    suggestionVibe === vibe
                                        ? 'bg-[#1b263b] text-white'
                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                }`}
                            >
                                {vibe}
                            </button>
                        ))}
                    </div>

                    {loadingSuggestions && (
                        <div className="text-center py-4">
                            <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                Generating suggestions...
                            </p>
                        </div>
                    )}

                    {aiSuggestions && !loadingSuggestions && (
                        <div className="space-y-4">
                            {['appetizers', 'entrees', 'sides', 'desserts'].map((category) => (
                                aiSuggestions[category] && aiSuggestions[category].length > 0 && (
                                    <div key={category}>
                                        <h4 className="font-sans text-base font-medium text-[#1b263b] mb-2 capitalize">
                                            {category}
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {aiSuggestions[category].map((recipe) => {
                                                const isSuggestion = recipe.is_suggestion || (typeof recipe.id === 'string' && recipe.id.startsWith('suggestion-'));
                                                return (
                                                    <div
                                                        key={recipe.id}
                                                        onClick={() => {
                                                            if (!isSuggestion) {
                                                                toggleRecipeSelection(recipe.id);
                                                            } else {
                                                                setError(`${recipe.name} is a suggestion. Please create it as a recipe first or search for an existing recipe.`);
                                                            }
                                                        }}
                                                        className={`bg-neutral-0 hairline p-2 transition-all ${
                                                            isSuggestion 
                                                                ? 'opacity-60 cursor-not-allowed' 
                                                                : 'cursor-pointer hover:bg-neutral-50'
                                                        } ${
                                                            !isSuggestion && selectedRecipes.has(recipe.id)
                                                                ? 'ring-2 ring-[#1b263b] bg-neutral-50'
                                                                : ''
                                                        }`}
                                                        title={isSuggestion ? 'Create this as a recipe first' : ''}
                                                    >
                                                        <div className="aspect-square w-full mb-1 bg-neutral-50 hairline overflow-hidden">
                                                            <SignedImage
                                                                src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                                                alt={recipe.name}
                                                                className="w-full h-full object-cover"
                                                                category={recipe.category}
                                                            />
                                                        </div>
                                                        <h5 className="font-sans text-xs font-medium text-neutral-900 line-clamp-2">
                                                            {recipe.name}
                                                        </h5>
                                                        {isSuggestion && (
                                                            <p className="font-mono text-xs text-neutral-900 opacity-40 mt-1">
                                                                Suggestion
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    )}
                    
                    {/* Create Recipe Button - Below AI Suggestions when a vibe is selected */}
                    {suggestionVibe && (
                        <div className="mt-4">
                            <button
                                onClick={handleCreateRecipe}
                                className="w-full px-4 py-3 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all font-mono text-sm uppercase tracking-wider flex items-center justify-center gap-2"
                            >
                                <span className="text-lg">+</span>
                                <span>Create New Recipe</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Search Controls */}
                <div className="mb-6 space-y-4">
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setSearchType('name');
                                setSearchQuery('');
                                setRecipes([]);
                            }}
                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                searchType === 'name'
                                    ? 'bg-[#1b263b] text-white'
                                    : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                            }`}
                        >
                            Name
                        </button>
                        <button
                            onClick={() => {
                                setSearchType('ingredient');
                                setSearchQuery('');
                                setRecipes([]);
                            }}
                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                searchType === 'ingredient'
                                    ? 'bg-[#1b263b] text-white'
                                    : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                            }`}
                        >
                            Ingredient
                        </button>
                        <button
                            onClick={() => {
                                setSearchType('vibe');
                                setSelectedVibe('');
                                setRecipes([]);
                            }}
                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                searchType === 'vibe'
                                    ? 'bg-[#1b263b] text-white'
                                    : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                            }`}
                        >
                            Vibe
                        </button>
                    </div>

                    {searchType !== 'vibe' ? (
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`Search by ${searchType}...`}
                            className="w-full px-4 py-2 bg-neutral-0 hairline font-sans text-base text-neutral-900 focus:outline-none focus:ring-2 focus:ring-[#1b263b]"
                        />
                    ) : (
                        <select
                            value={selectedVibe}
                            onChange={(e) => setSelectedVibe(e.target.value)}
                            className="w-full px-4 py-2 bg-neutral-0 hairline font-sans text-base text-neutral-900 focus:outline-none focus:ring-2 focus:ring-[#1b263b]"
                        >
                            <option value="">Select a vibe...</option>
                            {availableVibes.map(vibe => (
                                <option key={vibe} value={vibe}>
                                    {vibe}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-900 hairline font-mono text-sm">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-8">
                        <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                            Searching...
                        </p>
                    </div>
                )}

                {/* Recipe Results */}
                {!loading && recipes.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-3">
                            Search Results
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                            {recipes.map((recipe) => (
                                <div
                                    key={recipe.id}
                                    onClick={() => toggleRecipeSelection(recipe.id)}
                                    className={`bg-neutral-0 hairline p-3 cursor-pointer transition-all ${
                                        selectedRecipes.has(recipe.id)
                                            ? 'ring-2 ring-[#1b263b] bg-neutral-50'
                                            : 'hover:bg-neutral-50'
                                    }`}
                                >
                                    <div className="aspect-square w-full mb-2 bg-neutral-50 hairline overflow-hidden">
                                        <SignedImage
                                            src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                            alt={recipe.name}
                                            className="w-full h-full object-cover"
                                            category={recipe.category}
                                        />
                                    </div>
                                    <h3 className="font-sans text-sm font-medium text-neutral-900 mb-1 line-clamp-2">
                                        {recipe.name}
                                    </h3>
                                    {recipe.cooking_time && (
                                        <p className="font-mono text-xs text-neutral-900 opacity-60">
                                            {recipe.cooking_time}m
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* No Results */}
                {!loading && searchQuery && recipes.length === 0 && searchType !== 'vibe' && (
                    <div className="text-center py-8">
                        <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                            No recipes found
                        </p>
                    </div>
                )}

                {!loading && searchType === 'vibe' && !selectedVibe && (
                    <div className="text-center py-8">
                        <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                            Select a vibe to search
                        </p>
                    </div>
                )}

                        {/* Confirm Button - Biggest */}
                        {selectedRecipes.size > 0 && (
                            <div className="mt-6 pt-6 border-t hairline">
                                <button
                                    onClick={handleConfirmAdd}
                                    className="w-full px-6 py-4 bg-[#1b263b] text-white font-mono text-base uppercase tracking-wider transition-all border-none hover:opacity-60"
                                >
                                    Add {selectedRecipes.size} Recipe{selectedRecipes.size > 1 ? 's' : ''} to Schedule
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default RecipeSearchModal;
